/**
 * Defer bookkeeping work until the browser is idle.
 *
 * WHY THIS EXISTS
 * ---------------
 * The offline layer writes two things after a successful bootstrap: the
 * sessionStorage copy of `/shop/bootstrap` (a full-tree `JSON.stringify`) and
 * the IndexedDB read model used when the link drops. Both are pure bookkeeping
 * for the *next* load, and both used to run inline - which meant the catalogue
 * the cashier was waiting for appeared only after the browser had serialised
 * several hundred kB and opened a write transaction on the same thread.
 *
 * These helpers move that work behind the first paint:
 *
 *   whenIdle(task)        - run once, in an idle slot (or a short timeout where
 *                           `requestIdleCallback` does not exist, e.g. Safari or
 *                           jsdom).
 *   createIdleTask(task)  - the same, but *coalescing*: schedule as often as you
 *                           like and only the latest arguments are written, so a
 *                           reconnect storm cannot queue five identical writes.
 *
 * Neither helper is allowed to throw into the caller: a failed cache write must
 * never break a working till, so rejections are logged and swallowed. Both
 * return a cancel function, because a provider that unmounts mid-flight should
 * not keep writing to storage.
 */

/** How long to wait before falling back to `setTimeout` (no idle callbacks). */
const DEFAULT_FALLBACK_DELAY_MS = 300;

/** `requestIdleCallback` timeout: guarantees the task runs even on a busy tab. */
const DEFAULT_TIMEOUT_MS = 2000;

/** True when the browser can tell us when it is actually idle. */
export const supportsIdleCallback = () => {
  try {
    return typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function';
  } catch (_) {
    return false;
  }
};

const warnTaskFailure = (error) => {
  console.warn('[idle] deferred task failed', error?.message || error);
};

/**
 * Run `task` once, as soon as the browser has spare time.
 *
 * @returns {() => void} cancel function (safe to call at any point).
 */
export const whenIdle = (task, { timeoutMs = DEFAULT_TIMEOUT_MS, fallbackDelayMs = DEFAULT_FALLBACK_DELAY_MS } = {}) => {
  if (typeof task !== 'function') return () => {};

  let cancelled = false;
  let cancelScheduled = null;

  const run = () => {
    cancelScheduled = null;
    if (cancelled) return;
    try {
      const outcome = task();
      if (outcome && typeof outcome.then === 'function') {
        outcome.then(undefined, warnTaskFailure);
      }
    } catch (error) {
      warnTaskFailure(error);
    }
  };

  if (supportsIdleCallback()) {
    const handle = window.requestIdleCallback(run, { timeout: Math.max(0, Number(timeoutMs) || 0) });
    cancelScheduled = () => {
      if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(handle);
    };
  } else {
    const handle = setTimeout(run, Math.max(0, Number(fallbackDelayMs) || 0));
    cancelScheduled = () => clearTimeout(handle);
  }

  return () => {
    cancelled = true;
    if (typeof cancelScheduled === 'function') cancelScheduled();
    cancelScheduled = null;
  };
};

/**
 * A reusable idle slot that keeps only the newest arguments.
 *
 * `schedule(a)` then `schedule(b)` before the slot fires writes `b` and never
 * touches `a`; a `schedule` that arrives while the task is running is honoured
 * afterwards (one extra run, again with the newest arguments only).
 */
export const createIdleTask = (task, options = {}) => {
  if (typeof task !== 'function') throw new TypeError('createIdleTask requires a task function');

  let cancelScheduled = null;
  let pendingArgs = null;
  let hasPending = false;
  let running = false;

  const arm = () => {
    if (cancelScheduled || running || !hasPending) return;
    cancelScheduled = whenIdle(async () => {
      cancelScheduled = null;
      if (!hasPending) return;
      const args = pendingArgs;
      hasPending = false;
      pendingArgs = null;
      running = true;
      try {
        await task(...args);
      } finally {
        running = false;
        // Work arrived while we were busy: give it the next idle slot.
        if (hasPending) arm();
      }
    }, options);
  };

  return {
    schedule(...args) {
      pendingArgs = args;
      hasPending = true;
      arm();
    },
    cancel() {
      hasPending = false;
      pendingArgs = null;
      if (typeof cancelScheduled === 'function') cancelScheduled();
      cancelScheduled = null;
    },
    /** Diagnostics / tests: is a write still owed to the idle slot? */
    get pending() {
      return hasPending;
    }
  };
};
